// src/hooks/useLargeursAuto.ts — SCE Issues
// Largeur automatique des colonnes du tableau : on mesure, avec la vraie police,
// le texte le plus large de chaque colonne sur TOUTE la liste (donc stable quand
// on filtre ou qu'on replie un groupe), puis `largeurAuto` applique les bornes.
//
// La police est self-hostée : on attend qu'elle soit chargée, sinon la mesure se
// ferait avec la police de repli et les colonnes seraient trop étroites.

import { useEffect, useMemo, useState } from 'react'
import type { Issue } from '@/types/issue.types'
import {
  COLONNES_IDS,
  DEFINITIONS_COLONNES,
  largeurAuto,
  texteMesure,
  type ColonneId,
  type ContexteTableau,
} from '@/utils/tableauProblemes'

const TAILLE_TEXTE = 14
// Les en-têtes sont en capitales de 11 px, espacées de 0,06 em (globals.css).
const TAILLE_LIBELLE = 11
const ESPACEMENT_LIBELLE = 0.06 * TAILLE_LIBELLE

/** Espace entre deux pastilles / badges, déjà compté dans `extra` des colonnes. */
let contexteCanvas: CanvasRenderingContext2D | null | undefined

/** Largeur d'un texte avec la police de la page (canvas) ; aussi pour la liste des équipements. */
export function mesurerTexte(texte: string, taille: number, graisse: number): number {
  return mesurer(texte, taille, graisse)
}

function mesurer(texte: string, taille: number, graisse: number): number {
  if (contexteCanvas === undefined) contexteCanvas = document.createElement('canvas').getContext('2d')
  if (!contexteCanvas) return texte.length * taille * 0.55
  const famille = getComputedStyle(document.body).fontFamily
  contexteCanvas.font = `${graisse} ${taille}px ${famille}`
  return contexteCanvas.measureText(texte).width
}

export function useLargeursAuto(
  issues: readonly Issue[],
  ctx: ContexteTableau,
): Partial<Record<ColonneId, number>> {
  // Les polices chargées peuvent changer toutes les mesures : on recompte.
  const [policesPretes, setPolicesPretes] = useState(0)
  useEffect(() => {
    let annule = false
    void document.fonts?.ready.then(() => {
      if (!annule) setPolicesPretes((n) => n + 1)
    })
    return () => {
      annule = true
    }
  }, [])

  return useMemo(() => {
    void policesPretes
    const auto: Partial<Record<ColonneId, number>> = {}
    for (const col of COLONNES_IDS) {
      const def = DEFINITIONS_COLONNES[col]
      if (col === 'titre') continue
      const graisse = col === 'criticite' || col === 'statut' || col === 'badge' ? 500 : 400
      const textes = issues.map((i) => mesurer(texteMesure(col, i, ctx), TAILLE_TEXTE, graisse))
      // Le badge « +1 » des problèmes à plusieurs badges.
      const plus = col === 'badge' && issues.some((i) => (i.badgesCloture?.length ?? 0) > 1) ? 26 : 0
      auto[col] = largeurAuto(def, textes, mesurer(def.libelle.toUpperCase(), TAILLE_LIBELLE, 600) + def.libelle.length * ESPACEMENT_LIBELLE) + plus
    }
    return auto
    // ctx.maintenant change à chaque rendu : seule la liste et le modèle comptent ici.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issues, ctx.modeleDe, policesPretes])
}
